import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Backbone from '@trello/backbone';
import type { LabelColor } from '@trello/labels';
import { defaultRouter_DO_NOT_USE } from '@trello/router/legacy-router';
import { navigate } from '@trello/router/navigate';
import { useSharedState } from '@trello/shared-state';

import { showingBoardView } from 'app/scripts/controller/getCurrentBoardView';
import { ModelCache } from 'app/scripts/db/ModelCache';
import type { CardFilter } from 'app/scripts/view-models/CardFilter';
import { ID_NONE, NO_LABELS } from 'app/src/satisfies-filter';
import type { BoardActivityFilterString } from './filters/ActivityFilter';
import { ActivityFilter } from './filters/ActivityFilter';
import type { BoardDueFilterString, ViewFilter } from './filters';
import {
  CompleteFilter,
  DueFilter,
  LabelsFilter,
  MembersFilter,
  ModeFilter,
  SortFilter,
  TitleFilter,
} from './filters';
import { toQueryStringWithDecodedFilterParams } from './toQueryStringWithDecodedFilterParams';
import { FilterMode } from './types';
import { useUpdateBoardFiltersFromObjectRemoval } from './useUpdateBoardFiltersFromObjectRemoval';
import { useUrlParams } from './useUrlParams';
import { ViewFilters } from './ViewFilters';
import type {
  ViewFiltersContextValue,
  ViewFiltersSource,
} from './ViewFiltersContext';
import { ViewFiltersContext } from './ViewFiltersContext';
import { viewFiltersContextSharedState } from './viewFiltersContextSharedState';

interface BoardFilterType {
  idLabels?: string[];
  idMembers?: string[];
  due?: BoardDueFilterString;
  overdue?: boolean;
  dueComplete?: boolean;
  dateLastActivity?: BoardActivityFilterString;
  mode?: string;
  title?: string;
  notDue?: boolean;
}

export const updateBoardFilterFromViewsFilter = (
  idBoard: string,
  viewsFilter: ViewFilters,
) => {
  const board = ModelCache.get('Board', idBoard);

  const boardFilter = board?.filter;

  const newFilterArr = Object.entries(viewsFilter.toQueryParams());

  boardFilter?.clear();

  newFilterArr.forEach(([key, value]) => {
    switch (key) {
      case 'idMembers':
        {
          const idMembersArr = value?.split(',');
          idMembersArr?.forEach((member) => {
            boardFilter?.addIdMember(member);
          });
        }
        break;
      case 'labels': {
        const labelsArr = value?.split(',');
        const labelsList = board?.labelList.models;
        const labelsObj: {
          [key: string]: string;
        } = {};

        labelsList?.forEach((label) => {
          const labelColor = label.get('color');
          const labelName = label.get('name');

          const labelString = labelName
            ? `${labelColor}:${labelName}`
            : labelColor;
          const labelID = label.get('id');

          labelsObj[labelString] = labelID;
        });
        labelsArr?.forEach((label) => {
          if (label === 'none') {
            return boardFilter?.addIdLabel(ID_NONE);
          }
          const labelID = labelsObj[label];
          boardFilter?.addIdLabel(labelID);
        });
        break;
      }
      case 'due':
        {
          const [first, second, third] = value?.split(',') || [];
          const isOverdueString =
            second === 'overdue' || (!second && first === 'overdue');
          const isNotDueString =
            third === 'notdue' ||
            (!third && second === 'notdue') ||
            (!second && first === 'notdue');
          const rangeString =
            first !== 'overdue' && first !== 'notdue' ? first : null;

          boardFilter?.set('overdue', isOverdueString);
          boardFilter?.set('notDue', isNotDueString);

          if (rangeString) {
            boardFilter?.set('due', rangeString);
          }
        }
        break;
      case 'dateLastActivity':
        {
          const [rangeString] = value?.split(',') || [];
          if (rangeString) {
            boardFilter?.set('dateLastActivity', rangeString);
          }
        }
        break;
      case 'dueComplete':
        {
          if (value === 'true') {
            boardFilter?.set(key, true);
            break;
          }
          if (value === 'false') {
            boardFilter?.set(key, false);
            break;
          }
        }
        break;
      case 'title':
      case 'mode':
        boardFilter?.set(key, value);
        break;
      default:
        break;
    }
  });
};

export const useInitialUrlFilters = (idBoard: string) => {
  useEffect(() => {
    // First check if we have params in the URL to populate
    const queryParams =
      Object.fromEntries(new URLSearchParams(location.search).entries()) || {};
    const urlFilter = ViewFilters.fromQueryParams(queryParams);
    if (urlFilter.isFiltering()) {
      updateBoardFilterFromViewsFilter(idBoard, urlFilter);
    }
  }, [idBoard]);
};

export const useBoardFiltersFromModelCache = (idBoard: string) => {
  const modelCacheBoardFilters = useMemo(
    () => ModelCache.get('Board', idBoard)?.filter,
    [idBoard],
  );

  const [boardFilters, setBoardFilters] = useState<BoardFilterType>(
    modelCacheBoardFilters ? modelCacheBoardFilters.toJSON() : {},
  );

  useEffect(() => {
    if (!modelCacheBoardFilters) {
      return;
    }

    const onBoardFilterChange = (newBoardFilter: CardFilter) => {
      setBoardFilters(newBoardFilter.toJSON());
    };

    Backbone.Events.listenTo(
      modelCacheBoardFilters,
      'change:title change:idLabels change:idMembers change:due change:dateLastActivity change:overdue change:notDue change:dueComplete change:mode',
      onBoardFilterChange,
    );

    return () => {
      Backbone.Events.stopListening(
        modelCacheBoardFilters,
        'change:title change:idLabels change:idMembers change:due change:dateLastActivity change:overdue change:notDue change:dueComplete change:mode',
        onBoardFilterChange,
      );
    };
  }, [modelCacheBoardFilters]);

  return boardFilters;
};

export const parseBoardFilterObject = ({
  idLabels,
  idMembers,
  due,
  overdue,
  dueComplete,
  dateLastActivity,
  mode,
  title,
  notDue,
}: BoardFilterType): ViewFilters => {
  let members = new MembersFilter();

  if (idMembers) {
    idMembers.forEach((member) => {
      members = members.toggle(member);
    });
  }

  let labels = new LabelsFilter();
  if (idLabels) {
    idLabels.forEach((idLabel) => {
      if (idLabel === ID_NONE) {
        labels = labels.toggle([NO_LABELS, NO_LABELS]);
      }

      const label = ModelCache.get('Label', idLabel);

      if (label) {
        const color = label.get('color');
        const name = label.get('name');
        labels = labels.toggle([color as LabelColor, name]);
      }
    });
  }

  let dueFilter = due ? DueFilter.fromBoardString(due) : new DueFilter();

  if (overdue) {
    dueFilter = dueFilter.setOverdue(true);
  }
  if (notDue) {
    dueFilter = dueFilter.setNotDue(true);
  }

  if (dueComplete === true) {
    dueFilter = dueFilter.setCompleteFilter(CompleteFilter.Complete);
  } else if (dueComplete === false) {
    dueFilter = dueFilter.setCompleteFilter(CompleteFilter.Incomplete);
  }

  const activityFilter = dateLastActivity
    ? ActivityFilter.fromBoardString(dateLastActivity)
    : new ActivityFilter();

  const titleFilter = title
    ? new TitleFilter().setTitle(title)
    : new TitleFilter();

  const modeFilter =
    mode === FilterMode.And
      ? new ModeFilter().setMode(FilterMode.And)
      : new ModeFilter();

  return new ViewFilters({
    members,
    labels,
    due: dueFilter,
    title: titleFilter,
    mode: modeFilter,
    dateLastActivity: activityFilter,
  });
};

export const navigateToUrlFromFilterChange = (viewsFilters: ViewFilters) => {
  const routeContext = defaultRouter_DO_NOT_USE.getRoute();

  if (!routeContext?.url) {
    return;
  }

  const newUrlParams = viewsFilters.toQueryParams();
  const newUrl = new URL(routeContext.url.origin + routeContext.url.pathname);

  for (const [key, value] of Object.entries(newUrlParams)) {
    if (value) {
      newUrl.searchParams.set(key, value);
    }
  }

  if (routeContext.url.toString() === newUrl.toString()) {
    return;
  }

  const queryString = toQueryStringWithDecodedFilterParams(newUrl.searchParams);
  const search = queryString.length ? `?${queryString}` : '';

  navigate(`${newUrl.pathname}${search}`, { replace: true });
  defaultRouter_DO_NOT_USE.updateSubscribers();
};

const useSortFromUrl = function () {
  // Relies on useFixForBackboneNavigationBug() from SingleBoardTableView
  const urlParams = useUrlParams();

  const sortFilter = useMemo(() => {
    const sort = SortFilter.fromUrlParams(urlParams);
    return sort;
  }, [urlParams]);

  return sortFilter;
};

interface SingleBoardViewFiltersProviderProps {
  idBoard: string;
}

export const SingleBoardViewFiltersProvider: FunctionComponent<
  PropsWithChildren<SingleBoardViewFiltersProviderProps>
> = ({ children, idBoard }) => {
  useInitialUrlFilters(idBoard);

  const boardFilters = useBoardFiltersFromModelCache(idBoard);
  const sortFilter = useSortFromUrl();

  const viewsFilters = useMemo(() => {
    const filters = parseBoardFilterObject(boardFilters);
    return filters.setFilter(sortFilter);
  }, [boardFilters, sortFilter]);

  useEffect(() => {
    if (showingBoardView()) {
      navigateToUrlFromFilterChange(viewsFilters);
    }
  }, [viewsFilters]);

  const setFilter = useCallback(
    (filter: ViewFilter) => {
      if (filter.filterType === 'sort') {
        // For the sort order, the url is the the source of truth, since
        // boards don't have a sort order. For other filters, we use the
        // legacy BoardFilter backbone model as the source of truth
        // https://hello.atlassian.net/wiki/spaces/TRELLOFE/pages/1255292939/Trello+views+architecture#For-single-board-views%3A
        // TODO PANO-1855 Remove legacy filtering code
        navigateToUrlFromFilterChange(viewsFilters.setFilter(filter));
        return;
      }

      const board = ModelCache.get('Board', idBoard);
      const boardFilter = board?.filter;

      if (!boardFilter || !filter.serializeToBackboneFilter) {
        return;
      }

      const backboneFilterCriteria = filter.serializeToBackboneFilter(board);
      for (const key in backboneFilterCriteria) {
        boardFilter.set(
          key,
          backboneFilterCriteria[key as keyof typeof backboneFilterCriteria],
        );
      }
    },
    [idBoard, viewsFilters],
  );

  useUpdateBoardFiltersFromObjectRemoval(idBoard, viewsFilters, setFilter);

  const clearFilters = useCallback(() => {
    const board = ModelCache.get('Board', idBoard);
    board?.filter.clear();
  }, [idBoard]);

  const getCommonAttributes = useCallback(
    () => ({
      totalFilterLength: viewsFilters.totalFilterLength(['boards', 'sort']),
    }),
    [viewsFilters],
  );

  const [filterPopoverResult, setFilterPopoverResult] =
    useState<ViewFiltersSource['filterPopoverResult']>();

  const providerValue: ViewFiltersContextValue<ViewFiltersSource> = useMemo(
    () => ({
      viewFilters: {
        contextType: 'singleBoard',
        filters: viewsFilters,
        setFilter,
        clearFilters,
        resetFilters: clearFilters,
        getCommonAttributes,
        filterPopoverResult,
        setFilterPopoverResult,
      },
    }),
    [
      clearFilters,
      filterPopoverResult,
      getCommonAttributes,
      setFilter,
      viewsFilters,
    ],
  );

  const [, setSharedState] = useSharedState(viewFiltersContextSharedState);

  useEffect(() => {
    setSharedState(providerValue);
  }, [providerValue, setSharedState]);

  return (
    <ViewFiltersContext.Provider value={providerValue}>
      {children}
    </ViewFiltersContext.Provider>
  );
};
